import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ecbbClientes from 'assets/img/2026/ecbb_clientes.png';
import ecbbFinanceiro from 'assets/img/2026/ecbb_financeiro.png';
import ecbbAsg from 'assets/img/2026/ecbb_asg.png';
import ecbbTransformacao from 'assets/img/2026/ecbb_transformacao.png';
import ecbbCultura from 'assets/img/2026/ecbb_cultura.png';
import simplificacao from 'assets/img/2026/simplificacao.png';
import negocios from 'assets/img/2026/negocios.png';
import sustentacao from 'assets/img/2026/sustentacao.png';
import { Podio } from './Podio';
import './CategoriasParticipantes.scss';

interface CategoriaItem {
  id: string;
  nome: string;
  imagem: string;
}

interface CategoriasParticipantesProps {
  /**
   * Quando informado, ativa o modo "controlado": o clique chama
   * `onCategoriaChange` em vez de navegar para `/iniciativas`. A categoria
   * com este id recebe a borda amarela.
   */
  categoriaSelecionada?: string;
  onCategoriaChange?: (categoria: string) => void;
  title?: string;
  /**
   * Mapa de contadores por id de categoria. Quando presente, exibe um
   * indicador "X/limite" em cada card.
   */
  contadores?: Record<string, number>;
  limitePorCategoria?: number;
  /**
   * Variante visual. `etapa3` apenas troca o título — o fundo azul e o
   * troféu são aplicados no `.iniciativas-section` que envolve o componente
   * (ver HomePublic/style.scss `.iniciativas-section--etapa3`).
   */
  variant?: 'default' | 'etapa3';
}

export const CATEGORIAS_PARTICIPANTES: CategoriaItem[] = [
  { id: 'ECBB_CLIENTES', nome: 'ECBB - Clientes', imagem: ecbbClientes },
  { id: 'ECBB_FINANCEIRO', nome: 'ECBB - Financeira', imagem: ecbbFinanceiro },
  { id: 'ECBB_ASG', nome: 'ECBB - ASG', imagem: ecbbAsg },
  { id: 'ECBB_TDIGITAL', nome: 'ECBB - Transformação Digital e Processos', imagem: ecbbTransformacao },
  { id: 'ECBB_CULTURA_PESSOAS', nome: 'ECBB - Pessoas e Cultura', imagem: ecbbCultura },
  { id: 'SIMPLIFIQUE', nome: 'Simplificação e Eficiência', imagem: simplificacao },
  { id: 'CONEXAO', nome: 'Negócios e Atendimento', imagem: negocios },
  { id: 'PILAR', nome: 'Pilar de Sustentação', imagem: sustentacao },
];

const IDS_CONEXAO = new Set(CATEGORIAS_PARTICIPANTES.map((c) => c.id));

/**
 * Verifica se um voto preliminar (voto_preliminar_etapa3__c) pertence ao
 * produto Conexão. A collection é compartilhada com a ELBB — um preliminar é
 * Conexão quando suas notasRascunho contêm ao menos uma categoria da Etapa 3
 * do Prêmio BB (ECBB_*, SIMPLIFIQUE, CONEXAO, PILAR).
 */
export const isPreliminarConexao = (
  notasRascunho?: Record<string, unknown> | null
): boolean =>
  !!notasRascunho &&
  Object.keys(notasRascunho).some((cat) => IDS_CONEXAO.has(cat));

/**
 * Remove de um mapa de votos/notas as categorias que não pertencem ao produto
 * Conexão. rascunho_etapa3__c e voto_preliminar_etapa3__c são compartilhados
 * com a ELBB e podem trazer a chave 'ELBB' (e outras) misturada — filtrar por
 * categoria evita que votos ELBB contem no total/no submit da Conexão.
 */
export const filtrarNotasConexao = <T,>(
  notas?: Record<string, T> | null
): Record<string, T> => {
  if (!notas) return {};
  const out: Record<string, T> = {};
  Object.keys(notas).forEach((cat) => {
    if (IDS_CONEXAO.has(cat)) out[cat] = notas[cat];
  });
  return out;
};

export const CategoriasParticipantes: React.FC<CategoriasParticipantesProps> = ({
  categoriaSelecionada,
  onCategoriaChange,
  title,
  contadores,
  limitePorCategoria,
  variant = 'default',
}) => {
  const navigate = useNavigate();
  const isControlled = typeof onCategoriaChange === 'function';
  const isEtapa3 = variant === 'etapa3';

  // Estado local para controle quando não for um formulário controlado (ex: na home pública)
  const [localSelectedCategory, setLocalSelectedCategory] = useState('ECBB_CLIENTES');
  const activeCategory = isControlled ? (categoriaSelecionada || '') : localSelectedCategory;

  const computedTitle =
    title ??
    (isEtapa3
      ? 'Conheça as iniciativas vencedoras'
      : 'Conheça as iniciativas classificadas na Etapa 1');

  const handleClick = (id: string) => {
    if (isControlled) {
      onCategoriaChange?.(id);
      return;
    }
    if (isEtapa3) {
      // Na Home pública, alternar as categorias selecionadas localmente para atualizar o Pódio dinâmico
      setLocalSelectedCategory(id);
      return;
    }
    navigate('/iniciativas', { state: { categoria: id, filtroEtapa1: 'classificadas' } });
  };

  const currentCategoryName = CATEGORIAS_PARTICIPANTES.find(c => c.id === activeCategory)?.nome || '';

  return (
    <div className="categorias-participantes">
      <h2 className="categorias-participantes__title">{computedTitle}</h2>
      <div className="categorias-participantes__grid">
        {CATEGORIAS_PARTICIPANTES.map((categoria) => {
          const isActive = activeCategory === categoria.id;
          const contagem = contadores?.[categoria.id];
          const atingiuLimite =
            typeof limitePorCategoria === 'number' &&
            typeof contagem === 'number' &&
            contagem >= limitePorCategoria;

          const className = [
            'categorias-participantes__btn',
            isActive ? 'active' : '',
            atingiuLimite ? 'is-completed' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={categoria.id}
              type="button"
              className={className}
              onClick={() => handleClick(categoria.id)}
              aria-pressed={isControlled ? isActive : undefined}
              aria-label={`Filtrar por ${categoria.nome}`}
            >
              <img src={categoria.imagem} alt="" aria-hidden="true" />
              <span className="categorias-participantes__nome">{categoria.nome}</span>
            </button>
          );
        })}
      </div>

      {isEtapa3 && (
        <Podio categoriaId={activeCategory} categoriaNome={currentCategoryName} />
      )}
    </div>
  );
};

